import * as Api from '@ViewModels';

/**
 * Select option data for the admin Account Overview, ported from the legacy
 * `AccountDetails` container (`StageOptions` / `IndustryOptions` / `AccountCategoryOptions`)
 * and the `getBrokerDealerOptions` / `getSystemOfRecordOptions` helpers.
 *
 * The legacy options used the `ISelectBoxOption` shape (`{ title, value }`) and allowed a
 * `null` value for the broker-dealer "None" entry. The components-v2 `Select` only accepts
 * string item values, so these are adapted to `{ value: string, label: string }`. The two
 * legacy placeholder/none semantics are preserved as string sentinels:
 *  - broker-dealer "None" (legacy `value: null`) → `NONE_OPTION_VALUE` (the empty string),
 *    with `brokerDealerValueToApi` / `brokerDealerSelectionValue` round-tripping it to/from `null`;
 *  - system-of-record "Select One" placeholder keeps its legacy empty-string value.
 */

export interface IOverviewSelectOption {
	value: string;
	label: string;
}

/** Sentinel value for the broker-dealer "None" option (legacy value was `null`). */
export const NONE_OPTION_VALUE = '';

/** Every industry, mirroring the legacy `IndustryOptions` built from `Api.AllIndustries`. */
export const industryOptions: IOverviewSelectOption[] = [...Api.AllIndustries].map(industry => ({
	value: industry,
	label: industry,
}));

/** The three normalized lifecycle stages the dropdown offers (legacy `StageOptions`). */
export const stageOptions: IOverviewSelectOption[] = [
	{ value: Api.Stage.Implementation, label: Api.Stage.Implementation },
	{ value: Api.Stage.Success, label: Api.Stage.Success },
	{ value: Api.Stage.Support, label: Api.Stage.Support },
];

/** Account categories, None-first, mirroring the legacy `AccountCategoryOptions`. */
export const accountCategoryOptions: IOverviewSelectOption[] = [
	{ value: Api.AccountCategory.None, label: 'None' },
	{ value: Api.AccountCategory.BrokerDealer, label: 'Broker Dealer' },
	{ value: Api.AccountCategory.Levitate, label: 'Levitate' },
	{ value: Api.AccountCategory.Demo, label: 'Demo' },
];

/**
 * Broker-dealer options for the given industry, or `null` when the industry has none
 * (matching the legacy helper). The list is prefixed with a "None" option whose value is
 * the empty-string sentinel (the legacy option used `value: null`).
 */
export const getBrokerDealerOptions = (industry?: Api.Industry): IOverviewSelectOption[] | null => {
	const noneOption: IOverviewSelectOption = { value: NONE_OPTION_VALUE, label: 'None' };
	const toOptions = (dealers: readonly string[]): IOverviewSelectOption[] => [
		noneOption,
		...dealers.map(brokerDealer => ({ value: brokerDealer, label: brokerDealer })),
	];

	if (industry === Api.Industry.HomeMaintenance) {
		return toOptions(Api.AllHomeMaintenanceBrokerDealers);
	}
	if (industry === Api.Industry.Financial) {
		return toOptions(Api.AllFinancialBrokerDealers);
	}
	if (industry === Api.Industry.NonProfit) {
		return toOptions(Api.AllNonProfitBrokerDealers);
	}
	return null;
};

/**
 * System-of-record options for the given industry. Always prefixed with a "Select One"
 * empty-value placeholder (legacy `value: ''`). Chiropractic and Health use the dedicated
 * Chiro list in declaration order (Chiro accounts are now provisioned under Health, so
 * Health gets the same list — matching the legacy `getSystemOfRecordOptions` helper);
 * every other industry uses the general list sorted alphabetically.
 */
export const getSystemOfRecordOptions = (industry?: Api.Industry): IOverviewSelectOption[] => {
	const placeholder: IOverviewSelectOption = { value: '', label: 'Select One' };

	if (industry === Api.Industry.Chiropractic || industry === Api.Industry.Health) {
		return [
			placeholder,
			...Api.AllChiroSystemsOfRecord.map(systemOfRecord => ({ value: systemOfRecord, label: systemOfRecord })),
		];
	}

	return [
		placeholder,
		...[...Api.AllSystemsOfRecord].sort().map(systemOfRecord => ({ value: systemOfRecord, label: systemOfRecord })),
	];
};

/** Converts a broker-dealer select value back to the API shape (`null` for the None sentinel). */
export const brokerDealerValueToApi = (value: string): Api.BrokerDealer | null =>
	value === NONE_OPTION_VALUE ? null : (value as Api.BrokerDealer);

/**
 * Selected-industry value for the Select, mirroring legacy `getIndustrySelection`:
 * a known industry passes through; anything else (missing / legacy) falls back to 'Misc'.
 */
export const industrySelectionValue = (industry?: string | null): string =>
	industryOptions.some(option => option.value === industry) ? (industry as string) : Api.Industry.Miscellaneous;

/**
 * Selected account-category value for the Select, mirroring legacy `getAccountCategorySelection`:
 * a known category passes through; missing/unknown defaults to `None`.
 */
export const accountCategorySelectionValue = (category?: Api.AccountCategory | null): Api.AccountCategory =>
	accountCategoryOptions.some(option => option.value === category)
		? (category as Api.AccountCategory)
		: Api.AccountCategory.None;

/**
 * Selected-stage value for the Select, mirroring legacy `getStageSelection`: no stage →
 * `undefined` (shows the placeholder); a known or legacy stage passes through verbatim
 * (legacy stages still display even though they are not in `stageOptions`).
 */
export const stageSelectionValue = (stage?: string | null): string | undefined => (stage ? stage : undefined);

/**
 * Selected broker-dealer value for the Select: a `null`/missing broker dealer maps to the
 * None sentinel; an existing broker dealer passes through verbatim.
 */
export const brokerDealerSelectionValue = (brokerDealer?: Api.BrokerDealer | string | null): string =>
	brokerDealer ? brokerDealer : NONE_OPTION_VALUE;

/**
 * Selected system-of-record value for the Select: a missing value maps to the empty
 * placeholder value; an existing value passes through verbatim.
 */
export const systemOfRecordSelectionValue = (systemOfRecord?: string | null): string =>
	systemOfRecord ? systemOfRecord : '';
